// Mirrors apps/web/src/notify/useNotify.ts in AIF, scoped to the
// GHL-Lite console. Components import { useNotify } from this module
// instead of importing `toast` from sonner directly.

import { toast } from "sonner";
import { useMemo } from "react";

export type NotifyOptions = {
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  duration?: number;
  id?: string | number;
};

export type NotifyUpdateOptions = {
  type?: "info" | "success" | "error" | "loading";
  message?: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  } | null;
  duration?: number;
};

type NotifyInput = string | NotifyOptions | undefined;

function normalizeOptions(input?: NotifyInput): NotifyOptions {
  if (typeof input === "string") {
    return { description: input };
  }
  return input ?? {};
}

function optionsForSonner(options: NotifyOptions | NotifyUpdateOptions) {
  return {
    description: options.description,
    action: options.action === null ? undefined : options.action,
    duration: options.duration,
  };
}

export function useNotify() {
  return useMemo(
    () => ({
      info(message: string, options?: NotifyInput) {
        const normalized = normalizeOptions(options);
        return toast(message, {
          ...optionsForSonner(normalized),
          id: normalized.id,
        });
      },
      success(message: string, options?: NotifyInput) {
        const normalized = normalizeOptions(options);
        return toast.success(message, {
          ...optionsForSonner(normalized),
          id: normalized.id,
        });
      },
      error(message: string, options?: NotifyInput) {
        const normalized = normalizeOptions(options);
        return toast.error(message, {
          ...optionsForSonner(normalized),
          id: normalized.id,
        });
      },
      loading(message: string, options?: NotifyInput) {
        const normalized = normalizeOptions(options);
        return toast.loading(message, {
          ...optionsForSonner(normalized),
          id: normalized.id,
        });
      },
      update(id: string | number, options: NotifyUpdateOptions) {
        const message = options.message ?? "";
        const sonnerOptions = { ...optionsForSonner(options), id };
        if (options.type === "success") return toast.success(message, sonnerOptions);
        if (options.type === "error") return toast.error(message, sonnerOptions);
        if (options.type === "info") return toast.info(message, sonnerOptions);
        if (options.type === "loading") return toast.loading(message, sonnerOptions);
        return toast(message, sonnerOptions);
      },
      dismiss(id?: string | number) {
        toast.dismiss(id);
      },
      promise<T>(
        promise: Promise<T>,
        messages: {
          loading: string;
          success: string | ((value: T) => string);
          error: string | ((error: unknown) => string);
        },
      ) {
        return toast.promise(promise, messages);
      },
    }),
    [],
  );
}

export type Notify = ReturnType<typeof useNotify>;
